import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { SearchInput } from 'components/Form/DEPRECATED/FormElements';
import Flex from 'components/Flex';

import Screen from 'components/Screen';
import { MEDIUM_BREAK_POINT } from 'config/constants';

const ActionBarContainer = styled.div`
  margin: 1.5rem 0 2rem 0;

  @media (max-width: ${MEDIUM_BREAK_POINT}px) {
    padding: 0;
  }
`;

const ActionBarSearch = styled.div`
  display: flex;
  flex: 1 1 auto;
  margin-right: 1.5rem;
  max-width: 44rem;
  width: 100%;

  .form-group {
    margin-bottom: 0;
  }

  @media screen and (max-width: ${MEDIUM_BREAK_POINT}px) {
    margin-right: 0;
    max-width: none;
  }
`;

const ActionsContainer = styled.div`
  @media screen and (max-width: ${MEDIUM_BREAK_POINT}px) {
    display: flex;
    flex-grow: 1;
    margin-top: ${({ $hasSearch }) => ($hasSearch ? '1.5rem' : '0')};
  }
`;

const SearchAside = styled.div`
  @media screen and (max-width: ${MEDIUM_BREAK_POINT}px) {
    display: flex;
    flex: ${({ $searchInput }) => !$searchInput && 1};
    padding-left: ${({ $hasSearch }) => ($hasSearch ? '0.7rem' : '0')};
  }
`;

/**
 * [ActionBar] – All the action, all the time. Goes above the other stuff.
 * NOTE: "---" in comments below denotes areas that grow to container width
 * 
 * Desktop Layout:
 * [search] [renderAside]   --------   [renderActions]
 * 
 * Desktop Layout (w/out search):
 * [renderAside]   -----------------   [renderActions]
 * 
 * Desktop Layout (just renderActions):
 *  --------------------------------   [renderActions]
 * 
 * Mobile Layout:
 * [ --- search --- ][renderAside]
 * [ ------ renderActions ------ ]
 * 
 * Mobile Layout (w/out search):
 * [renderAside]                      NOTE: renderAside will fit screen width if width is 100%
 * [ ------ renderActions ------ ]

 * @param {boolean} [searchInput] - Signals desire for searching goodness and passes all props into <SearchInput />
 * @param {function} [renderAside] - Render prop for cool stuff to right of optional <SearchInput />>
 * @param {function} [renderActions] - Render prop for EXTREME ACTION at the far right side.
 * @param {boolean} loading - Please wait...
 */

const ActionBar = ({ searchInput, renderAside, renderActions, loading }) => (
  <ActionBarContainer>
    <Screen.MEDIUM up>
      <Flex grow={1}>
        {searchInput && (
          <ActionBarSearch>
            <SearchInput disabled={loading} {...searchInput} />
          </ActionBarSearch>
        )}

        {renderAside && renderAside()}

        {renderActions && (
          <Flex direction="column" justify="flex-end">
            {renderActions()}
          </Flex>
        )}
      </Flex>
    </Screen.MEDIUM>

    <Screen.MEDIUM down>
      <Flex grow={1} shrink={0} expand>
        {searchInput && (
          <ActionBarSearch>
            <SearchInput disabled={loading} {...searchInput} />
          </ActionBarSearch>
        )}

        {renderAside && (
          <SearchAside $hasSearch={searchInput} $searchInput={searchInput}>
            {renderAside()}
          </SearchAside>
        )}
      </Flex>

      {renderActions && (
        <ActionsContainer $hasSearch={searchInput}>
          <Flex grow={1} shrink={1} direction="column" justify="flex-end">
            {renderActions && renderActions()}
          </Flex>
        </ActionsContainer>
      )}
    </Screen.MEDIUM>
  </ActionBarContainer>
);

ActionBar.propTypes = {
  searchInput: PropTypes.shape({
    // See search input for valid props
  }),
  loading: PropTypes.bool,
  renderAside: PropTypes.func,
  renderActions: PropTypes.func
};

export default ActionBar;
